import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useI18n } from "@/i18n";
import { Section, Eyebrow } from "@/components/PublicLayout";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

export default function Invest() {
  const { t, field, fieldList } = useI18n();
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", organisation: "", ticket_range: "", message: "" });
  const [sent, setSent] = useState(false);
  useEffect(() => { api.get("/investor").then((r) => setData(r.data)); }, []);
  if (!data) return <Section className="py-24"><p className="text-ink/50">{t("loading")}</p></Section>;

  const STATE_LABEL = { "PRE-LAUNCH": t("inv_state_pre"), LIVE: t("inv_state_live"), CLOSED: t("inv_state_closed") };

  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/investor/leads", form); setSent(true); toast.success(t("inv_thanks")); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  return (
    <div>
      <div className="bg-ink text-paper">
        <Section className="py-20 md:py-28">
          <div className="inline-block border border-heraldic/40 text-heraldic text-xs px-3 py-1 rounded-sm mb-6">{data.campaign_status}</div>
          <h1 className="font-serif text-4xl md:text-7xl tracking-tight max-w-4xl">{t("inv_h1")}</h1>
          <p className="mt-6 max-w-2xl text-paper/70 text-lg leading-relaxed">{field(data, "thesis")}</p>
        </Section>
      </div>

      <Section className="py-16 md:py-24 grid lg:grid-cols-3 gap-14">
        <div className="lg:col-span-2 space-y-12">
          <div>
            <Eyebrow>{t("inv_ecosystem")}</Eyebrow>
            <ul className="space-y-3">{fieldList(data, "ecosystem").map((e, i) => <li key={i} className="flex gap-3 text-ink/75"><span className="font-mono text-heraldic text-sm mt-1">0{i + 1}</span>{e}</li>)}</ul>
          </div>
          <div>
            <Eyebrow>{t("inv_covers")}</Eyebrow>
            <div className="flex flex-wrap gap-2">{data.sections.map((s) => <span key={s} className="border border-border text-ink/60 text-xs px-3 py-1.5 rounded-sm">{s}</span>)}</div>
          </div>
          <div>
            <Eyebrow>{t("inv_risks")}</Eyebrow>
            <ul className="space-y-2">{fieldList(data, "risks").map((r, i) => <li key={i} className="text-ink/70">• {r}</li>)}</ul>
          </div>
          <div className="border border-border rounded-sm p-6 bg-secondary/40">
            <div className="eyebrow text-ink/40 mb-2">{t("inv_issuer")}</div>
            <div className="grid sm:grid-cols-2 gap-2 text-sm text-ink/70">
              {Object.entries(data.issuer).map(([k, v]) => v && <div key={k}><span className="text-ink/40">{k.replace(/_/g, " ")}: </span>{v}</div>)}
            </div>
            <p className="mt-4 text-xs text-ink/45">{field(data, "disclaimer")}</p>
          </div>
        </div>

        <aside className="lg:col-span-1">
          <div className="border border-border rounded-sm p-6 sticky top-24">
            <div className="font-serif text-2xl text-ink">{STATE_LABEL[data.campaign_status]}</div>
            {sent ? (
              <p className="mt-4 text-heraldic">{t("inv_thanks")}</p>
            ) : (
              <form onSubmit={submit} className="mt-5 space-y-3">
                <Input data-testid="invest-name" required placeholder={t("inv_f_name")} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-sm" />
                <Input data-testid="invest-email" required type="email" placeholder={t("inv_f_email")} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-sm" />
                <Input data-testid="invest-org" placeholder={t("inv_f_org")} value={form.organisation} onChange={(e) => setForm({ ...form, organisation: e.target.value })} className="rounded-sm" />
                <Input data-testid="invest-ticket" placeholder={t("inv_f_ticket")} value={form.ticket_range} onChange={(e) => setForm({ ...form, ticket_range: e.target.value })} className="rounded-sm" />
                <Textarea data-testid="invest-message" placeholder={t("inv_f_msg")} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="rounded-sm" />
                <button data-testid="invest-submit" className="w-full bg-heraldic text-white py-3 rounded-sm hover:bg-heraldic-700 transition-colors">{STATE_LABEL[data.campaign_status]}</button>
              </form>
            )}
          </div>
        </aside>
      </Section>
    </div>
  );
}
